/* ============================================================
   Laley PDP Clean v4 — gallery
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Registrerer metoder på LaleyPDP.prototype via PDP.registerModule.
   Krever pdp-clean-v4-core.js lastet først (defer-rekkefølge i product-clean-v3-1.liquid).
   ============================================================ */
(() => {
  const PDP = window.LaleyPDPCleanV4;
  if (!PDP || typeof PDP.registerModule !== 'function') {
    console.error('[PDP] Kjerne mangler — hopper over modul: gallery');
    return;
  }
  const { lockRootScroll, unlockRootScroll } = PDP.utils;

  PDP.registerModule('gallery', {
    bindGalleryZoom() {
      // High-performance PDP lightbox:
      // - open instantly with the already-loaded product image
      // - swap to decoded high-res image when ready
      // - preload neighbor images so next/prev feels immediate
      // - one transform pipeline for zoom/pan to avoid mousemove jank
      if (!this.gallery) return;
      const getItems = () => Array.from(this.gallery.querySelectorAll('.pdp-clean__gallery-item:not([data-pdp-gallery-loop-clone])'))
        .filter((item) => item.dataset.mediaType === 'image' && item.querySelector('img'));
      const items = getItems();
      if (!items.length) return;

      let lightbox = null, lbImg = null, lbCounter = null, lbCloseBtn = null;
      let stage = null;
      let currentIdx = 0, lastFocused = null;
      let loadToken = 0;
      let raf = null;
      let scale = 1;
      let pan = { x: 0, y: 0 };
      let ignoreClickUntil = 0;
      let galleryPointer = null;
      let pendingGalleryZoomItem = null;
      let suppressGalleryZoomUntil = 0;
      let scrollLockToken = null;
      const ZOOM = 2.5;
      const MAX_ZOOM = 4;
      const preloadCache = new Map();

      const normalizedIndex = (idx) => {
        const count = getItems().length || 1;
        return (idx + count) % count;
      };

      const fullSrcFor = (img) => {
        const src = img?.currentSrc || img?.src || '';
        if (!src) return '';
        try {
          const url = new URL(src, window.location.href);
          if (url.searchParams.has('width')) {
            url.searchParams.set('width', '2200');
            return url.toString();
          }
        } catch (error) { /* fall through to string replacement */ }
        return src
          .replace(/width=\d+/i, 'width=2200')
          .replace(/_(\d{2,4})x(\d{0,4})?(\.|@)/, '_2200x$3');
      };

      const imageData = (idx) => {
        const list = getItems();
        const item = list[normalizedIndex(idx)];
        const img = item?.querySelector('img');
        const thumb = img?.currentSrc || img?.src || img?.getAttribute('src') || '';
        return {
          alt: img?.alt || '',
          full: fullSrcFor(img) || thumb,
          thumb,
        };
      };

      const preloadImage = (idx, priority = false) => {
        const data = imageData(idx);
        if (!data.full) return Promise.resolve('');
        const cached = preloadCache.get(data.full);
        if (cached) {
          if (priority && cached.image?.fetchPriority) cached.image.fetchPriority = 'high';
          return cached.promise;
        }

        const probe = new Image();
        probe.decoding = 'async';
        probe.loading = 'eager';
        if ('fetchPriority' in probe) probe.fetchPriority = priority ? 'high' : 'low';

        const record = { image: probe, loaded: false, promise: null };
        record.promise = new Promise((resolve) => {
          probe.onload = () => resolve(data.full);
          probe.onerror = () => resolve(data.full);
          probe.src = data.full;
          if (probe.decode) {
            probe.decode().then(() => resolve(data.full)).catch(() => resolve(data.full));
          }
        }).then((src) => {
          record.loaded = true;
          return src;
        });
        preloadCache.set(data.full, record);
        return record.promise;
      };

      const warmVisibleImages = () => {
        [0, 1, 2].forEach((idx) => preloadImage(idx, idx === 0));
      };
      // Ytelse (audit #4): idle-warming lastet ~1,4MB 2200px-bilder per
      // sidevisning uten brukerintensjon. Warm i stedet ved FØRSTE
      // pointer-kontakt med galleriet — god margin før tap/zoom åpner
      // lightboxen, og null kost for de som aldri rører galleriet.
      if (this.gallery) {
        this.on(this.gallery, 'pointerdown', warmVisibleImages, { once: true, passive: true });
        this.on(this.gallery, 'touchstart', warmVisibleImages, { once: true, passive: true });
      }

      const zoomBlockedSelector = [
        'a',
        'button',
        'input',
        'select',
        'textarea',
        'summary',
        '[contenteditable="true"]',
        '[data-pdp-model-toggle]',
        '[data-pdp-gallery-prev]',
        '[data-pdp-gallery-next]',
        '[data-pdp-gallery-video]',
        'video',
        'iframe',
        '.pdp-clean__wishlist-slot',
      ].join(',');

      const itemFromZoomEvent = (event) => {
        if (!(event.target instanceof Element)) return null;
        const item = event.target.closest('.pdp-clean__gallery-item');
        if (!item || !this.gallery.contains(item)) return null;
        if (item.dataset.mediaType !== 'image' || !item.querySelector('img')) return null;
        const blocked = event.target.closest(zoomBlockedSelector);
        if (blocked && blocked !== item && !blocked.classList.contains('pdp-clean__gallery-item')) return null;
        return item;
      };

      const clampPan = () => {
        if (!stage || !lbImg || scale <= 1) {
          pan = { x: 0, y: 0 };
          return;
        }
        const stageRect = stage.getBoundingClientRect();
        const baseW = lbImg.offsetWidth || lbImg.getBoundingClientRect().width;
        const baseH = lbImg.offsetHeight || lbImg.getBoundingClientRect().height;
        const maxX = Math.max(0, ((baseW * scale) - stageRect.width) / 2);
        const maxY = Math.max(0, ((baseH * scale) - stageRect.height) / 2);
        pan.x = Math.max(-maxX, Math.min(maxX, pan.x));
        pan.y = Math.max(-maxY, Math.min(maxY, pan.y));
      };

      const applyTransform = (withTransition = true) => {
        if (!lbImg) return;
        clampPan();
        if (withTransition) {
          lbImg.classList.remove('is-dragging');
        } else {
          lbImg.classList.add('is-dragging');
        }
        window.cancelAnimationFrame(raf);
        raf = window.requestAnimationFrame(() => {
          if (!lbImg) return;
          lbImg.classList.toggle('is-zoomed', scale > 1);
          lbImg.style.transform = scale > 1
            ? `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`
            : '';
        });
      };

      const resetZoom = () => {
        scale = 1;
        pan = { x: 0, y: 0 };
        if (lbImg) {
          lbImg.classList.remove('is-zoomed', 'is-dragging');
          lbImg.style.transform = '';
        }
      };

      const zoomAt = (x, y) => {
        if (!lbImg) return;
        if (scale > 1) {
          resetZoom();
          return;
        }
        const rect = lbImg.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const dx = x - (rect.left + rect.width / 2);
        const dy = y - (rect.top + rect.height / 2);
        scale = ZOOM;
        pan = {
          x: -dx * (ZOOM - 1),
          y: -dy * (ZOOM - 1),
        };
        applyTransform(true);
      };

      const ensureLightbox = () => {
        if (lightbox) return;
        lightbox = document.createElement('div');
        lightbox.className = 'pdp-clean__lightbox';
        lightbox.setAttribute('role', 'dialog');
        lightbox.setAttribute('aria-modal', 'true');
        lightbox.setAttribute('aria-label', 'Bildevisning');
        lightbox.tabIndex = -1;
        lightbox.innerHTML = `
          <button type="button" class="pdp-clean__lightbox-close" aria-label="Lukk">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>
          </button>
          <button type="button" class="pdp-clean__lightbox-prev" aria-label="Forrige bilde">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
          </button>
          <button type="button" class="pdp-clean__lightbox-next" aria-label="Neste bilde">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>
          </button>
          <div class="pdp-clean__lightbox-stage">
            <img class="pdp-clean__lightbox-img" alt="" />
          </div>
          <div class="pdp-clean__lightbox-counter" aria-live="polite"></div>
        `;
        document.body.appendChild(lightbox);
        this.lightbox = lightbox;
        lbImg = lightbox.querySelector('.pdp-clean__lightbox-img');
        lbCounter = lightbox.querySelector('.pdp-clean__lightbox-counter');
        lbCloseBtn = lightbox.querySelector('.pdp-clean__lightbox-close');
        stage = lightbox.querySelector('.pdp-clean__lightbox-stage');

        this.on(lbCloseBtn, 'click', close);
        this.on(lightbox.querySelector('.pdp-clean__lightbox-prev'), 'click', prev);
        this.on(lightbox.querySelector('.pdp-clean__lightbox-next'), 'click', next);
        this.on(lightbox, 'click', (e) => { if (e.target === lightbox) close(); });

        this.on(stage, 'click', (e) => {
          if (e.target !== lbImg && e.target !== stage) return;
          if (performance.now() < ignoreClickUntil) return;
          // Touch håndteres av tap/dobbel-tap i pointer-gestene under —
          // enkelt-tap skal IKKE zoome på mobil (old school-følelsen).
          if (e.pointerType === 'touch') return;
          zoomAt(e.clientX, e.clientY);
        });

        // Moderne touch-modell: pinch-to-zoom (to pekere, skalering rundt
        // midtpunktet), én-finger-pan når zoomet, dobbel-tap inn/ut, swipe
        // sidelengs = forrige/neste og swipe NED = lukk (uzoomet). Ingen
        // dekorativ motion — kun den eksisterende funksjonelle snap-en.
        const pointers = new Map();
        let pinch = null;
        let panAnchor = null;
        let tapStart = null;
        let lastTap = 0;
        let lastTapX = 0;
        let lastTapY = 0;

        this.on(stage, 'pointerdown', (e) => {
          if (e.target.closest('button')) return;
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          stage.setPointerCapture?.(e.pointerId);
          if (pointers.size === 2) {
            const [a, b] = [...pointers.values()];
            pinch = {
              dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
              scale,
              mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
              pan: { ...pan },
            };
            panAnchor = null;
            tapStart = null;
            e.preventDefault();
          } else if (pointers.size === 1) {
            panAnchor = { x: e.clientX, y: e.clientY, pan: { ...pan } };
            tapStart = { x: e.clientX, y: e.clientY, t: performance.now() };
            if (scale > 1) e.preventDefault();
          }
        }, { passive: false });

        this.on(stage, 'pointermove', (e) => {
          if (!pointers.has(e.pointerId)) return;
          pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
          if (pinch && pointers.size >= 2) {
            e.preventDefault();
            const [a, b] = [...pointers.values()];
            const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
            const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
            const nextScale = Math.max(1, Math.min(MAX_ZOOM, pinch.scale * (dist / pinch.dist)));
            const k = nextScale / pinch.scale;
            const rect = stage.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            pan = {
              x: mid.x - cx - k * (pinch.mid.x - cx - pinch.pan.x),
              y: mid.y - cy - k * (pinch.mid.y - cy - pinch.pan.y),
            };
            scale = nextScale;
            applyTransform(false);
            return;
          }
          if (panAnchor && scale > 1) {
            e.preventDefault();
            pan = {
              x: panAnchor.pan.x + (e.clientX - panAnchor.x),
              y: panAnchor.pan.y + (e.clientY - panAnchor.y),
            };
            applyTransform(false);
          }
        }, { passive: false });

        const releasePointer = (e) => {
          if (!pointers.has(e.pointerId)) return null;
          const last = { x: e.clientX, y: e.clientY };
          pointers.delete(e.pointerId);
          stage.releasePointerCapture?.(e.pointerId);
          return last;
        };

        this.on(stage, 'pointerup', (e) => {
          const last = releasePointer(e);
          if (!last) return;
          if (e.pointerType === 'touch') ignoreClickUntil = performance.now() + 400;
          if (pointers.size === 1) {
            // Fra pinch til én finger: re-forankre pan uten hopp
            const [rest] = [...pointers.values()];
            pinch = null;
            panAnchor = { x: rest.x, y: rest.y, pan: { ...pan } };
            tapStart = null;
            return;
          }
          if (pointers.size > 0) return;
          pinch = null;
          lbImg.classList.remove('is-dragging');

          const start = tapStart;
          const anchor = panAnchor;
          tapStart = null;
          panAnchor = null;

          // TAP / DOBBEL-TAP (touch): to raske tap nær hverandre → zoom inn/ut
          if (start && e.pointerType === 'touch') {
            const tapDist = Math.hypot(last.x - start.x, last.y - start.y);
            const now = performance.now();
            if (tapDist < 8 && now - start.t < 350) {
              if (now - lastTap < 300 && Math.hypot(last.x - lastTapX, last.y - lastTapY) < 48) {
                lastTap = 0;
                zoomAt(last.x, last.y);
                return;
              }
              lastTap = now;
              lastTapX = last.x;
              lastTapY = last.y;
            }
          }

          if (anchor) {
            const dx = last.x - anchor.x;
            const dy = last.y - anchor.y;
            if (scale <= 1) {
              // Swipe ned = lukk; sidelengs = forrige/neste
              if (dy > 70 && Math.abs(dy) > Math.abs(dx) * 1.4) {
                ignoreClickUntil = performance.now() + 320;
                close();
                return;
              }
              if (Math.abs(dx) > 54 && Math.abs(dx) > Math.abs(dy) * 1.45) {
                ignoreClickUntil = performance.now() + 320;
                dx < 0 ? next() : prev();
                return;
              }
            }
            if (Math.abs(dx) > 4 || Math.abs(dy) > 4) ignoreClickUntil = performance.now() + 260;
          }

          if (scale > 1 && scale < 1.05) {
            resetZoom();
            return;
          }
          applyTransform(true);
        });

        this.on(stage, 'pointercancel', (e) => {
          releasePointer(e);
          if (pointers.size < 2) pinch = null;
          if (pointers.size === 0) {
            panAnchor = null;
            tapStart = null;
            lbImg.classList.remove('is-dragging');
            applyTransform(true);
          }
        });

        this.on(lightbox, 'wheel', (e) => {
          if (!lightbox.classList.contains('is-open')) return;
          e.preventDefault();
          const delta = e.deltaY < 0 ? 0.18 : -0.18;
          const nextScale = Math.max(1, Math.min(MAX_ZOOM, scale + delta));
          if (nextScale === 1) {
            resetZoom();
            return;
          }
          if (scale === 1) {
            const rect = lbImg.getBoundingClientRect();
            pan = {
              x: -(e.clientX - (rect.left + rect.width / 2)) * (nextScale - 1),
              y: -(e.clientY - (rect.top + rect.height / 2)) * (nextScale - 1),
            };
          }
          scale = nextScale;
          applyTransform(false);
        }, { passive: false });
      };

      const show = (idx) => {
        ensureLightbox();
        const count = getItems().length;
        if (!count) return;
        currentIdx = (idx + count) % count;
        const token = ++loadToken;
        const data = imageData(currentIdx);
        if (!data.thumb && !data.full) return;
        resetZoom();
        lbImg.alt = data.alt;
        if (data.thumb && lbImg.src !== data.thumb) lbImg.src = data.thumb;
        lightbox.classList.add('is-loading');
        lbCounter.textContent = `${currentIdx + 1} / ${count}`;
        lightbox.classList.add('is-open');
        scrollLockToken = lockRootScroll(scrollLockToken || Symbol('pdp-lightbox-lock'));
        if (lbCloseBtn) window.requestAnimationFrame(() => lbCloseBtn.focus());

        const cached = preloadCache.get(data.full);
        const setLoaded = () => lightbox?.classList.remove('is-loading');
        if (cached?.loaded) {
          if (lbImg.src !== data.full) lbImg.src = data.full;
          setLoaded();
        } else {
          preloadImage(currentIdx, true).then((fullSrc) => {
            if (token !== loadToken || currentIdx !== normalizedIndex(idx) || !lightbox?.classList.contains('is-open')) return;
            if (fullSrc && lbImg.src !== fullSrc) lbImg.src = fullSrc;
            const done = () => {
              if (token === loadToken) setLoaded();
            };
            if (lbImg.decode) lbImg.decode().then(done).catch(done);
            else done();
          });
        }
        preloadImage(currentIdx + 1);
        preloadImage(currentIdx - 1);
      };
      const showItem = (item) => {
        const list = getItems();
        const idx = list.indexOf(item);
        if (idx < 0) return;
        lastFocused = item;
        show(idx);
      };
      const close = () => {
        if (!lightbox) return;
        lightbox.classList.remove('is-open');
        lightbox.classList.remove('is-loading');
        resetZoom();
        unlockRootScroll(scrollLockToken);
        scrollLockToken = null;
        if (lastFocused?.focus) lastFocused.focus();
      };
      const prev = () => show(currentIdx - 1);
      const next = () => show(currentIdx + 1);

      this.on(this.gallery, 'pointerdown', (event) => {
        const item = itemFromZoomEvent(event);
        if (!item || event.button !== 0) return;
        galleryPointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          item,
        };
        pendingGalleryZoomItem = item;
      }, { capture: true, passive: true });

      this.on(this.gallery, 'pointerup', (event) => {
        if (!galleryPointer || galleryPointer.id !== event.pointerId) return;
        const dx = event.clientX - galleryPointer.x;
        const dy = event.clientY - galleryPointer.y;
        galleryPointer = null;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          pendingGalleryZoomItem = null;
          suppressGalleryZoomUntil = performance.now() + 260;
        }
      }, { capture: true, passive: true });

      this.on(this.gallery, 'pointercancel', () => {
        galleryPointer = null;
        pendingGalleryZoomItem = null;
        suppressGalleryZoomUntil = performance.now() + 260;
      }, { capture: true, passive: true });

      this.on(this.gallery, 'click', (event) => {
        if (event.defaultPrevented || performance.now() < suppressGalleryZoomUntil) return;
        const item = itemFromZoomEvent(event) || pendingGalleryZoomItem;
        pendingGalleryZoomItem = null;
        if (!item) return;
        event.__pdpZoomHandled = true;
        showItem(item);
      }, { capture: true });

      items.forEach((item, idx) => {
        item.style.cursor = 'zoom-in';
        item.tabIndex = window.matchMedia('(min-width: 990px)').matches && !item.classList.contains('is-active') ? -1 : 0;
        item.setAttribute('role', 'button');
        item.setAttribute('aria-label', `Forstørr bilde ${idx + 1} av ${items.length}`);
        this.on(item, 'mouseenter', () => preloadImage(idx, true), { passive: true });
        this.on(item, 'focus', () => preloadImage(idx, true), { passive: true });
        this.on(item, 'click', (e) => {
          if (e.defaultPrevented || e.__pdpZoomHandled || performance.now() < suppressGalleryZoomUntil) return;
          showItem(item);
        });
        this.on(item, 'keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          showItem(item);
        });
      });

      this.on(document, 'keydown', (e) => {
        if (!lightbox || !lightbox.classList.contains('is-open')) return;
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
        else if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          scale = Math.min(MAX_ZOOM, scale > 1 ? scale + 0.35 : ZOOM);
          applyTransform(true);
        } else if (e.key === '-') {
          e.preventDefault();
          scale = Math.max(1, scale - 0.35);
          if (scale === 1) resetZoom();
          else applyTransform(true);
        }
      });

      // Cleanup på destroy
      this.unsubs.push(() => {
        window.cancelAnimationFrame(raf);
        if (lightbox) {
          lightbox.remove();
          lightbox = null;
          this.lightbox = null;
        }
        unlockRootScroll(scrollLockToken);
        scrollLockToken = null;
      });
    },

    syncGalleryToVariant(variant, behavior = 'smooth') {
      if (!this.gallery) return;

      this.gallery.querySelectorAll('[data-pdp-gallery-loop-clone]').forEach((clone) => clone.remove());
      const items = Array.from(this.gallery.querySelectorAll('.pdp-clean__gallery-item:not([data-pdp-gallery-loop-clone])'))
        .sort((a, b) => Number(a.dataset.originalIndex || 0) - Number(b.dataset.originalIndex || 0));
      const featured = variant?.featuredMediaId
        ? items.find((item) => String(item.dataset.mediaId) === String(variant.featuredMediaId))
        : null;

      const orderedItems = featured ? [featured, ...items.filter((item) => item !== featured)] : items;
      orderedItems.forEach((item) => this.gallery.appendChild(item));

      this.prepareGalleryLoop?.();
      if (this.refreshGalleryStage) this.refreshGalleryStage(0, behavior);
      else if (window.matchMedia('(max-width: 749px)').matches) this.gallery.scrollTo({ left: 0, behavior });
      this.requestGalleryProgressUpdate?.();
    },

    bindGallery() {
      const isDesktopStage = () => window.matchMedia('(min-width: 990px)').matches;
      const prevButton = this.section.querySelector('[data-pdp-gallery-prev]');
      const nextButton = this.section.querySelector('[data-pdp-gallery-next]');
      const LOOP_CLONE_SELECTOR = '[data-pdp-gallery-loop-clone="true"]';
      const REAL_ITEM_SELECTOR = `.pdp-clean__gallery-item:not(${LOOP_CLONE_SELECTOR})`;
      const LOOP_BUFFER_MAX = 3;
      const SCROLL_SETTLE_MS = 140;
      const SCROLL_SNAP_TOLERANCE = 2;
      let galleryItemCount = 0;

      const allGalleryItems = () => this.gallery
        ? Array.from(this.gallery.querySelectorAll('.pdp-clean__gallery-item'))
        : [];
      const galleryItems = () => this.gallery
        ? Array.from(this.gallery.querySelectorAll(REAL_ITEM_SELECTOR))
        : [];
      const refreshGalleryCount = () => {
        if (!this.gallery) return;
        galleryItemCount = galleryItems().length;
      };

      const itemRealIndex = (item) => {
        const parsed = Number(item?.dataset?.pdpGalleryRealIndex);
        return Number.isFinite(parsed) ? parsed : -1;
      };
      const loopBufferSize = (count) => count > 1 ? Math.min(LOOP_BUFFER_MAX, count) : 0;

      const prepareClone = (source, realIndex) => {
        const clone = source.cloneNode(true);
        clone.dataset.pdpGalleryLoopClone = 'true';
        clone.dataset.pdpGalleryRealIndex = String(realIndex);
        clone.classList.remove('is-active');
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        try { clone.inert = true; } catch (error) { /* inert is progressive enhancement */ }
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        /* Modellinfo hører til det EKTE første bildet — klonen skal ikke vise dublett-chip. */
        clone.querySelectorAll('[data-pdp-model-toggle], [data-pdp-model-overlay]').forEach((el) => el.remove());
        clone.querySelectorAll('a, button, input, select, textarea, summary, video, iframe, [tabindex], [role="button"]').forEach((el) => {
          el.tabIndex = -1;
          el.setAttribute('aria-hidden', 'true');
        });
        return clone;
      };
      const cloneFragment = (items) => {
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(prepareClone(item, itemRealIndex(item))));
        return fragment;
      };

      const prepareLoopItems = () => {
        if (!this.gallery) return;
        this.gallery.querySelectorAll(LOOP_CLONE_SELECTOR).forEach((clone) => clone.remove());
        const items = galleryItems();
        items.forEach((item, idx) => {
          item.dataset.pdpGalleryRealIndex = String(idx);
          item.removeAttribute('data-pdp-gallery-loop-clone');
        });
        const loopBuffer = loopBufferSize(items.length);
        if (loopBuffer) {
          this.gallery.insertBefore(cloneFragment(items.slice(-loopBuffer)), items[0]);
          this.gallery.appendChild(cloneFragment(items.slice(0, loopBuffer)));
        }
        refreshGalleryCount();
      };
      this.prepareGalleryLoop = prepareLoopItems;
      prepareLoopItems();

      const clampStageIndex = (index) => Math.max(0, Math.min(galleryItemCount - 1, Number(index) || 0));
      const wrapStageIndex = (index) => galleryItemCount
        ? ((Number(index) % galleryItemCount) + galleryItemCount) % galleryItemCount
        : 0;
      const stageIndex = () => clampStageIndex(this.galleryStageIndex);
      const realItemForIndex = (index) => galleryItems().find((item) => itemRealIndex(item) === index) || galleryItems()[index];
      const nearestItemFromScroll = (scrollLeft = this.gallery?.scrollLeft || 0) => {
        const items = allGalleryItems();
        if (!items.length) return null;
        return items.reduce((closest, item) => {
          if (!closest) return item;
          return Math.abs(item.offsetLeft - scrollLeft) < Math.abs(closest.offsetLeft - scrollLeft) ? item : closest;
        }, null);
      };
      const realIndexFromScroll = (scrollLeft = this.gallery?.scrollLeft || 0) => {
        const fallback = stageIndex();
        const item = nearestItemFromScroll(scrollLeft);
        const index = itemRealIndex(item);
        return index >= 0 ? clampStageIndex(index) : fallback;
      };
      const progressIndexFromScroll = () => {
        const items = galleryItems();
        if (!this.gallery || galleryItemCount <= 1 || items.length <= 1) return stageIndex();
        const step = Math.max(1, (items[1]?.offsetLeft || 0) - (items[0]?.offsetLeft || 0) || this.gallery.clientWidth || 1);
        const raw = (this.gallery.scrollLeft - items[0].offsetLeft) / step;
        if (raw < 0 || raw > galleryItemCount - 1) return stageIndex();
        return Math.max(0, Math.min(galleryItemCount - 1, raw));
      };
      const reflectActiveStage = (nextIndex) => {
        const desktop = isDesktopStage();
        this.gallery?.classList.add('is-stage-ready');
        allGalleryItems().forEach((item) => {
          const clone = item.matches(LOOP_CLONE_SELECTOR);
          const active = !clone && itemRealIndex(item) === nextIndex;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', clone || (desktop && !active) ? 'true' : 'false');
          item.tabIndex = clone ? -1 : (desktop ? (active ? 0 : -1) : 0);
        });
      };

      const setStageIndex = (index = 0, options = {}) => {
        refreshGalleryCount();
        if (!galleryItemCount) return;
        const wrap = options.wrap !== false;
        const nextIndex = wrap ? wrapStageIndex(index) : clampStageIndex(index);
        this.galleryStageIndex = nextIndex;
        reflectActiveStage(nextIndex);
        if (options.scroll !== false) {
          const activeItem = realItemForIndex(nextIndex);
          if (activeItem) {
            const left = activeItem.offsetLeft;
            const behavior = options.behavior || 'smooth';
            this.gallery.scrollTo({ left, behavior });
          }
        }
        this.requestGalleryProgressUpdate?.();
      };

      const moveStage = (direction, options = {}) => {
        refreshGalleryCount();
        if (galleryItemCount <= 1 || !direction) return false;
        const current = stageIndex();
        const next = options.wrap === false ? clampStageIndex(current + direction) : wrapStageIndex(current + direction);
        if (next === current) return false;
        setStageIndex(next, options);
        return true;
      };

      this.refreshGalleryStage = (index = this.galleryStageIndex || 0, behavior = 'auto') => {
        this.prepareGalleryLoop?.();
        refreshGalleryCount();
        setStageIndex(index, { wrap: true, behavior });
      };

      if (prevButton) {
        this.on(prevButton, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          moveStage(-1);
        });
      }
      if (nextButton) {
        this.on(nextButton, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          moveStage(1);
        });
      }

      if (!this.gallery || !this.galleryProgressBar) return;

      const interactiveSelector = 'a, button, input, select, textarea, summary, video, iframe, [data-pdp-gallery-video], [role="button"], [contenteditable="true"]';
      const isRealGalleryControl = (target) => {
        if (!(target instanceof Element)) return false;
        const interactive = target.closest(interactiveSelector);
        if (!interactive) return false;
        return !interactive.classList.contains('pdp-clean__gallery-item');
      };
      const syncStageFromScroll = () => {
        if (galleryItemCount <= 1) return;
        const nextIndex = realIndexFromScroll();
        if (nextIndex === this.galleryStageIndex) return;
        this.galleryStageIndex = nextIndex;
        reflectActiveStage(nextIndex);
      };

      let quietJumpFrame = 0;
      const jumpToStageQuietly = (targetIndex) => {
        if (!this.gallery) return;
        window.cancelAnimationFrame(quietJumpFrame);
        const target = realItemForIndex(targetIndex);
        if (!target) return;
        this.gallery.classList.add('is-quick-jump');
        this.gallery.scrollTo({ left: target.offsetLeft, behavior: 'auto' });
        quietJumpFrame = window.requestAnimationFrame(() => {
          quietJumpFrame = window.requestAnimationFrame(() => {
            this.gallery?.classList.remove('is-quick-jump');
            this.requestGalleryProgressUpdate?.();
          });
        });
      };

      const normalizeLoopPosition = () => {
        refreshGalleryCount();
        if (galleryItemCount <= 1 || !this.gallery) return;
        const nearest = nearestItemFromScroll();
        if (!nearest?.matches(LOOP_CLONE_SELECTOR)) return;
        const realIndex = realIndexFromScroll();
        this.galleryStageIndex = realIndex;
        reflectActiveStage(realIndex);
        jumpToStageQuietly(realIndex);
      };
      this.normalizeGalleryLoop = normalizeLoopPosition;

      let settleScrollFrame = 0;
      let snapTargetLeft = null;
      let snapReleaseTimer = 0;
      const clearSnapTarget = () => {
        snapTargetLeft = null;
        window.clearTimeout(snapReleaseTimer);
      };
      const holdSnapTarget = (left) => {
        snapTargetLeft = left;
        window.clearTimeout(snapReleaseTimer);
        snapReleaseTimer = window.setTimeout(clearSnapTarget, 420);
      };
      const snapToNearestImage = () => {
        refreshGalleryCount();
        if (!this.gallery || galleryItemCount <= 1) return;
        if (snapTargetLeft !== null) {
          if (Math.abs(this.gallery.scrollLeft - snapTargetLeft) > SCROLL_SNAP_TOLERANCE) return;
          clearSnapTarget();
        }
        const nearest = nearestItemFromScroll();
        if (!nearest) return;
        const realIndex = realIndexFromScroll(nearest.offsetLeft);
        this.galleryStageIndex = realIndex;
        reflectActiveStage(realIndex);
        if (nearest.matches(LOOP_CLONE_SELECTOR)) {
          jumpToStageQuietly(realIndex);
          return;
        }
        const delta = Math.abs(this.gallery.scrollLeft - nearest.offsetLeft);
        if (delta <= SCROLL_SNAP_TOLERANCE) return;
        holdSnapTarget(nearest.offsetLeft);
        this.gallery.scrollTo({ left: nearest.offsetLeft, behavior: 'smooth' });
      };
      const settleGalleryScroll = () => {
        window.cancelAnimationFrame(settleScrollFrame);
        settleScrollFrame = window.requestAnimationFrame(() => {
          if (isDesktopStage()) {
            syncStageFromScroll();
            normalizeLoopPosition();
            return;
          }
          snapToNearestImage();
        });
      };

      this.on(this.gallery, 'wheel', (event) => {
        clearSnapTarget();
        if (!isDesktopStage() || galleryItemCount <= 1 || !event.cancelable) return;
        if (isRealGalleryControl(event.target)) return;
        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);
        if (absX < 12 || absX < absY * 1.35) return;
        event.preventDefault();
        this.gallery.scrollBy({ left: event.deltaX, behavior: 'auto' });
        this.requestGalleryProgressUpdate?.();
      }, { passive: false });
      this.on(this.gallery, 'touchstart', clearSnapTarget, { passive: true });

      let pointerDrag = null;
      let lastPointerDown = 0;
      const dragId = (event) => typeof event.pointerId === 'number' ? event.pointerId : 'mouse';
      const beginDrag = (event, id) => {
        pointerDrag = {
          id,
          index: stageIndex(),
          x: event.clientX,
          y: event.clientY,
          moved: false,
          intent: null,
        };
        if (id !== 'mouse') this.gallery.setPointerCapture?.(id);
        this.gallery.classList.add('is-dragging');
      };
      const resetDrag = (event) => {
        if (!pointerDrag || (event && dragId(event) !== pointerDrag.id)) return;
        if (pointerDrag.id !== 'mouse' && this.gallery.hasPointerCapture?.(pointerDrag.id)) this.gallery.releasePointerCapture(pointerDrag.id);
        this.gallery.classList.remove('is-dragging');
        pointerDrag = null;
      };
      const updateDrag = (event) => {
        if (!pointerDrag || dragId(event) !== pointerDrag.id) return;
        const dx = event.clientX - pointerDrag.x;
        const dy = event.clientY - pointerDrag.y;
        if (!pointerDrag.intent && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          pointerDrag.intent = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'horizontal' : 'vertical';
        }
        if (pointerDrag.intent === 'horizontal') {
          pointerDrag.moved = true;
          event.preventDefault();
        }
      };
      const finishDrag = (event) => {
        if (!pointerDrag || dragId(event) !== pointerDrag.id) return;
        const dx = event.clientX - pointerDrag.x;
        const dy = event.clientY - pointerDrag.y;
        const shouldMove = pointerDrag.intent === 'horizontal' && Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.15;
        const direction = dx < 0 ? 1 : -1;
        const dragged = pointerDrag.moved;
        const targetIndex = pointerDrag.index + direction;
        resetDrag(event);
        if (!shouldMove) return;
        if (dragged) event.preventDefault();
        setStageIndex(targetIndex);
      };

      this.on(this.gallery, 'pointerdown', (event) => {
        clearSnapTarget();
        if (!isDesktopStage() || galleryItemCount <= 1 || event.button !== 0) return;
        if (isRealGalleryControl(event.target)) return;
        lastPointerDown = Date.now();
        beginDrag(event, event.pointerId);
      });

      this.on(this.gallery, 'pointermove', updateDrag);
      this.on(this.gallery, 'pointerup', finishDrag);
      this.on(this.gallery, 'pointercancel', resetDrag);
      this.on(this.gallery, 'lostpointercapture', resetDrag);

      this.on(this.gallery, 'mousedown', (event) => {
        if (!isDesktopStage() || galleryItemCount <= 1 || event.button !== 0) return;
        if (pointerDrag || Date.now() - lastPointerDown < 120) return;
        if (isRealGalleryControl(event.target)) return;
        beginDrag(event, 'mouse');
      });
      this.on(document, 'mousemove', updateDrag);
      this.on(document, 'mouseup', finishDrag);

      this.on(document, 'keydown', (event) => {
        if (!isDesktopStage() || galleryItemCount <= 1) return;
        if (this.lightbox?.classList.contains('is-open')) return;
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        const target = event.target;
        if (target && target !== document.body) {
          if (target.closest('input, textarea, select, [contenteditable="true"], [data-pdp-tabs], [data-pdp-dropdown], .pdp-clean__picker, .pdp-clean__sizeguide')) return;
        }
        event.preventDefault();
        moveStage(event.key === 'ArrowRight' ? 1 : -1);
      });

      const update = () => {
        this.raf = null;
        refreshGalleryCount();
        syncStageFromScroll();
        const count = galleryItemCount;
        if (count <= 1) return;
        const segment = 100 / count;
        this.galleryProgressBar.style.width = `${segment}%`;
        this.galleryProgressBar.style.left = '0';
        this.galleryProgressBar.style.transform = `translateX(${progressIndexFromScroll() * 100}%)`;
      };

      this.requestGalleryProgressUpdate = () => {
        if (this.raf) return;
        this.raf = window.requestAnimationFrame(update);
      };

      let scrollEndTimer = 0;
      this.on(this.gallery, 'scroll', () => {
        this.requestGalleryProgressUpdate();
        window.clearTimeout(scrollEndTimer);
        scrollEndTimer = window.setTimeout(() => {
          settleGalleryScroll();
          this.requestGalleryProgressUpdate();
        }, SCROLL_SETTLE_MS);
      }, { passive: true });
      if ('onscrollend' in window) {
        this.on(this.gallery, 'scrollend', () => {
          refreshGalleryCount();
          settleGalleryScroll();
          this.requestGalleryProgressUpdate();
        }, { passive: true });
      }
      this.on(window, 'resize', () => { this.refreshGalleryStage?.(); this.requestGalleryProgressUpdate(); }, { passive: true });
      this.on(window, 'orientationchange', () => { this.refreshGalleryStage?.(); this.requestGalleryProgressUpdate(); }, { passive: true });
      setStageIndex(0, { behavior: 'auto' });
      this.requestGalleryProgressUpdate();
      this.bindGalleryVideoAutoplay?.();
    },

    bindGalleryVideoAutoplay() {
      // Autoplay-styring for muted loop-videoer i mediescrolleren: spill når
      // videoen er synlig i scrolleren/viewporten, pause utenfor (batteri +
      // båndbredde). Gjelder også loop-kloner. play() kan avvises (iOS
      // strømsparing) — da står posteren, aldri en feil.
      if (!this.gallery || !('IntersectionObserver' in window)) return;
      // Redusert bevegelse: respekter systemvalget — video forblir pauset
      // (poster/første frame), brukeren kan fortsatt spille manuelt.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.gallery.querySelectorAll('video').forEach((video) => {
          video.removeAttribute('autoplay');
          video.pause();
        });
        return;
      }
      const videos = () => this.gallery.querySelectorAll('video');
      if (!videos().length) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio > 0.4) {
            const attempt = video.play();
            if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      }, { root: null, threshold: [0, 0.45] });
      const observeAll = () => videos().forEach((video) => {
        if (video.dataset.pdpAutoplayObserved === 'true') return;
        video.dataset.pdpAutoplayObserved = 'true';
        video.muted = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        observer.observe(video);
      });
      observeAll();
      // Loop-kloner lages etter init — plukk opp nye videoer når de dukker opp.
      const cloneWatcher = new MutationObserver(observeAll);
      cloneWatcher.observe(this.gallery, { childList: true, subtree: true });
      this.unsubs.push(() => {
        observer.disconnect();
        cloneWatcher.disconnect();
      });
    },
  });
})();
